import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import './Banner.css';
import api from '../../api/axios';
import { API_IMAGE_BASE_URL } from '../../config/api';

const API_BASE = API_IMAGE_BASE_URL;

const PAGE_OPTIONS = [
  { value: 'home', label: '🏠 Home' },
  { value: 'about', label: 'ℹ️ About' },
  { value: 'vendor', label: '🏪 Vendor' },
  { value: 'budget', label: '💰 Budget Calculator' },
];

const Banner = () => {
  const [banners, setBanners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [page, setPage] = useState('');
  const [existingImages, setExistingImages] = useState([]); // string paths already on server
  const [newFiles, setNewFiles] = useState([]);             // new File objects to upload
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(null);   // lightbox

  const fetchBanners = async () => {
    try {
      const res = await api.get('/banners');
      setBanners(res.data?.data || []);
    } catch {
      toast.error('Failed to fetch banners');
    }
  };

  useEffect(() => { fetchBanners(); }, []);

  /* ── open modal ── */
  const openCreate = () => {
    setEditingBanner(null);
    setPage('');
    setExistingImages([]);
    setNewFiles([]);
    setShowModal(true);
  };

  const openEdit = (banner) => {
    setEditingBanner(banner);
    setPage(banner.page);
    setExistingImages(banner.images || []);
    setNewFiles([]);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBanner(null);
    setPage('');
    setExistingImages([]);
    setNewFiles([]);
  };

  /* ── image handlers ── */
  const handleFileAdd = (e) => {
    const files = Array.from(e.target.files);
    const total = existingImages.length + newFiles.length + files.length;
    if (total > 10) { toast.error('Maximum 10 images allowed'); return; }
    setNewFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removeExisting = (idx) =>
    setExistingImages(prev => prev.filter((_, i) => i !== idx));

  const removeNew = (idx) =>
    setNewFiles(prev => prev.filter((_, i) => i !== idx));

  /* ── submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (existingImages.length + newFiles.length === 0) {
      toast.error('At least one image is required');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('page', page);

      if (editingBanner) {
        // Tell backend which existing images to keep
        fd.append('existingImages', JSON.stringify(existingImages));
        newFiles.forEach(f => fd.append('images', f));
        await api.put(`/admin/banners/${editingBanner._id}`, fd);
        toast.success('Banner updated successfully');
      } else {
        newFiles.forEach(f => fd.append('images', f));
        await api.post('/admin/banners', fd);
        toast.success('Banner created successfully');
      }

      closeModal();
      fetchBanners();
    } catch (err) {
      if (!(err.response?.status === 413)) {
        toast.error(err.response?.data?.message || 'Failed to save banner');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── delete ── */
  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/banners/${deleteTarget._id}`);
      toast.success('Banner deleted');
      setDeleteTarget(null);
      fetchBanners();
    } catch {
      toast.error('Failed to delete banner');
    }
  };

  const totalImages = existingImages.length + newFiles.length;

  return (
    <div className="banner-container">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1>Banner Management</h1>
          <p>Manage hero banners for each page. Up to 10 images per page.</p>
        </div>
        <button className="btn-primary" onClick={openCreate}>+ Add Banner</button>
      </div>

      {/* ── Page filter tabs ── */}
      <div className="banner-page-tabs">
        {['all', ...PAGE_OPTIONS.map(p => p.value)].map(tab => (
          <button
            key={tab}
            className={`tab-btn${!window.__bannerTab || window.__bannerTab === tab ? '' : ''}`}
            onClick={() => {
              document.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
              document.getElementById(`tab-${tab}`)?.classList.add('active');
              document.querySelectorAll('.banner-card').forEach(c => {
                c.style.display =
                  tab === 'all' || c.dataset.page === tab ? '' : 'none';
              });
            }}
            id={`tab-${tab}`}
            style={tab === 'all' ? { fontWeight: 700, borderBottom: '2px solid #007bff' } : {}}
          >
            {tab === 'all' ? 'All Pages' : PAGE_OPTIONS.find(p => p.value === tab)?.label}
          </button>
        ))}
      </div>

      {/* ── Grid ── */}
      <div className="banners-grid">
        {banners.length === 0 ? (
          <div className="no-banners">
            <div style={{ fontSize: 48 }}>🖼️</div>
            <h3>No Banners Yet</h3>
            <p>Click "Add Banner" to upload your first hero banner</p>
          </div>
        ) : (
          banners.map(banner => (
            <div key={banner._id} className="banner-card" data-page={banner.page}>
              {/* image strip */}
              <div className="banner-images-container">
                {(banner.images || []).map((img, i) => (
                  <div
                    key={i}
                    className="banner-image-wrapper"
                    onClick={() => setPreviewIndex({ banner, i })}
                    title="Click to preview"
                  >
                    <img
                      src={img.startsWith('http') ? img : `${API_BASE}${img}`}
                      alt={`${banner.page} ${i + 1}`}
                      onError={e => { e.target.src = 'https://via.placeholder.com/400x200?text=Image+Not+Found'; }}
                    />
                    <div className="img-count-badge">{i + 1}/{banner.images.length}</div>
                  </div>
                ))}
              </div>

              <div className="banner-info">
                <div className="banner-meta">
                  <h3>{PAGE_OPTIONS.find(p => p.value === banner.page)?.label || banner.page}</h3>
                  <span className="image-count-chip">{banner.images?.length || 0} image{banner.images?.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="banner-actions">
                  <button className="btn-edit" onClick={() => openEdit(banner)}>✏️ Edit</button>
                  <button className="btn-delete" onClick={() => setDeleteTarget(banner)}>🗑️ Delete</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal banner-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingBanner ? '✏️ Edit Banner' : '➕ Add Banner'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">

                {/* Page select */}
                <div className="form-group">
                  <label>Page *</label>
                  <select
                    value={page}
                    onChange={e => setPage(e.target.value)}
                    required
                    disabled={!!editingBanner}
                  >
                    <option value="">Select page</option>
                    {PAGE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {editingBanner && (
                    <p className="file-note">Page cannot be changed after creation.</p>
                  )}
                </div>

                {/* Image counter */}
                <div className="image-counter-bar">
                  <span className={totalImages >= 10 ? 'counter-full' : ''}>
                    {totalImages} / 10 images
                  </span>
                  {totalImages > 0 && (
                    <span className="counter-sub">{totalImages} will be saved</span>
                  )}
                </div>

                {/* Upload zone */}
                {totalImages < 10 && (
                  <div
                    className="image-upload-area"
                    onClick={() => document.getElementById('bannerFileInput').click()}
                  >
                    <input
                      id="bannerFileInput"
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileAdd}
                    />
                    <div className="upload-content">
                      <div className="upload-icon">☁️</div>
                      <p>Click to upload images</p>
                      <p className="file-note">JPG / PNG / WebP — max 400 KB each</p>
                    </div>
                  </div>
                )}

                {/* Existing images (edit mode) */}
                {existingImages.length > 0 && (
                  <div>
                    <p className="section-label">Saved images (click × to remove)</p>
                    <div className="banner-images-preview-grid">
                      {existingImages.map((img, i) => (
                        <div key={i} className="preview-image-wrapper">
                          <img
                            src={img.startsWith('http') ? img : `${API_BASE}${img}`}
                            alt={`existing ${i}`}
                            onError={e => { e.target.src = 'https://via.placeholder.com/100?text=?'; }}
                          />
                          <button type="button" className="remove-img-btn" onClick={() => removeExisting(i)}>×</button>
                          <span className="img-badge">Saved</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New files preview */}
                {newFiles.length > 0 && (
                  <div>
                    <p className="section-label">New images to upload</p>
                    <div className="banner-images-preview-grid">
                      {newFiles.map((file, i) => (
                        <div key={i} className="preview-image-wrapper">
                          <img src={URL.createObjectURL(file)} alt={`new ${i}`} />
                          <button type="button" className="remove-img-btn" onClick={() => removeNew(i)}>×</button>
                          <span className="img-badge new-badge">New</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Specs */}
                <div className="banner-specs-info">
                  <h4>💡 Recommended Specs</h4>
                  <div className="specs-grid">
                    <div className="spec-item">
                      <strong>All pages:</strong>
                      <ul>
                        <li>Size: 1920 × 1080 px (16:9)</li>
                        <li>Minimum: 1600 × 900 px</li>
                        <li>Format: JPG / WebP &lt; 400 KB</li>
                      </ul>
                    </div>
                  </div>
                  <div className="important-notes">
                    <strong>Tips:</strong>
                    <ul>
                      <li>Keep text/logo in the <strong>center 60%</strong>.</li>
                      <li>Avoid critical content near edges (mobile crop).</li>
                      <li>Keep all slides the same size if uploading multiple.</li>
                    </ul>
                  </div>
                </div>

              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading || totalImages === 0}>
                  {loading ? 'Saving...' : editingBanner ? 'Update Banner' : 'Create Banner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete confirmation ── */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>🗑️ Delete Banner</h2>
              <button className="close-btn" onClick={() => setDeleteTarget(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>Delete the <strong>{PAGE_OPTIONS.find(p => p.value === deleteTarget.page)?.label}</strong> banner? This cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn-delete" onClick={confirmDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Lightbox preview ── */}
      {previewIndex && (
        <div className="lightbox-overlay" onClick={() => setPreviewIndex(null)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setPreviewIndex(null)}>×</button>
            <img
              src={
                previewIndex.banner.images[previewIndex.i].startsWith('http')
                  ? previewIndex.banner.images[previewIndex.i]
                  : `${API_BASE}${previewIndex.banner.images[previewIndex.i]}`
              }
              alt="preview"
            />
            <p className="lightbox-caption">
              {PAGE_OPTIONS.find(p => p.value === previewIndex.banner.page)?.label} — Image {previewIndex.i + 1} of {previewIndex.banner.images.length}
            </p>
            <div className="lightbox-nav">
              <button
                onClick={() => setPreviewIndex(prev => ({ ...prev, i: Math.max(0, prev.i - 1) }))}
                disabled={previewIndex.i === 0}
              >← Prev</button>
              <button
                onClick={() => setPreviewIndex(prev => ({ ...prev, i: Math.min(prev.banner.images.length - 1, prev.i + 1) }))}
                disabled={previewIndex.i === previewIndex.banner.images.length - 1}
              >Next →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Banner;
